import Toast from 'react-native-toast-message';
import {makeAutoObservable, runInAction} from 'mobx';

import {ERROR_MESSAGE, SUCCESS_CHECK} from '../components/ToastBottom';
import {
  PaymentService,
  PaymentServiceType,
  TillPaymentInstrument,
} from '../models/mfl';
import apiService from '../transport/apiService';

import type {RootStore} from './rootStore';

export const SPECIALS_TILE = 'SPECIALS_TILE';

const isTillPaymentService = (service: PaymentService) =>
  service.service_type === PaymentServiceType.Till;
const isZenGiftCardsPaymentService = (service: PaymentService) =>
  service.service_type === PaymentServiceType.ZenGiftCards;
const isTillPaymentInstrument = (instrument: TillPaymentInstrument) =>
  instrument.service_type === PaymentServiceType.Till;

export class PaymentStore {
  rootStore: RootStore;

  // Payment Services: The payment options that the store offers
  tillPaymentService: PaymentService | null = null;
  zenGiftCardsPaymentService: PaymentService | null = null;
  isLoadingPaymentServices = false;

  // Payment Instruments (a.k.a "payment methods"): The payment instruments that the user has.
  // The users can pay with:
  // - Credit cards: Through the TILL payment service. We only support 1 credit card in the UI.
  // - Zen Gift Cards: Not associated with the account, but with the order
  //   where they are used. For that reason they are not stored here.
  tillUserPaymentInstrument: TillPaymentInstrument | null = null; // one credit card or none associated with the account
  isLoadingUserPaymentInstruments = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  reset = () =>
    runInAction(() => {
      this.tillPaymentService = null;
      this.tillUserPaymentInstrument = null;
      this.isLoadingUserPaymentInstruments = true;
    });

  loadPaymentServicesForStore = async () => {
    let tillPaymentService: PaymentService | null = null;
    let zenGiftCardsPaymentService: PaymentService | null = null;

    runInAction(() => (this.isLoadingPaymentServices = true));
    try {
      const paymentServices =
        (await apiService.getAvailablePaymentServices()) as unknown as PaymentService[];

      tillPaymentService = paymentServices.find(isTillPaymentService) || null;
      zenGiftCardsPaymentService =
        paymentServices.find(isZenGiftCardsPaymentService) || null;
    } catch (err) {
      console.log(err);
    }
    runInAction(() => {
      this.tillPaymentService = tillPaymentService;
      this.zenGiftCardsPaymentService = zenGiftCardsPaymentService;
      this.isLoadingPaymentServices = false;
    });
  };

  getTokenizationDataForTillService = async () =>
    apiService.getTokenizationData(this.tillPaymentService?.id);

  createTillUserPaymentInstrument = async (token: any) =>
    apiService.createPaymentInstrument(this.tillPaymentService?.id, token);

  // Currently we only support one  (Till) credit card in the UI
  deleteTillUserPaymentInstrument = async () => {
    try {
      await apiService.deletePaymentInstrument(
        this.tillUserPaymentInstrument?.id,
      );
      runInAction(() => (this.tillUserPaymentInstrument = null));
      Toast.show({type: SUCCESS_CHECK});
    } catch (err: any) {
      Toast.show({type: ERROR_MESSAGE, text1: err.message});
    }
  };

  loadTillPaymentInstrumentForUser = async () => {
    let tillPaymentInstrument: TillPaymentInstrument | null = null;
    runInAction(() => (this.isLoadingUserPaymentInstruments = true));
    try {
      const paymentInstruments =
        (await apiService.getAvailableInstruments()) as unknown as TillPaymentInstrument[];
      tillPaymentInstrument =
        paymentInstruments.find(isTillPaymentInstrument) || null;
    } catch (err) {
      console.error(err);
    }
    runInAction(() => {
      this.tillUserPaymentInstrument = tillPaymentInstrument;
      this.isLoadingUserPaymentInstruments = false;
    });
  };

  setTillUserPaymentInstrument = (instrument: TillPaymentInstrument) =>
    runInAction(() => (this.tillUserPaymentInstrument = instrument));
}
